/**
 * InputRouter - DOM 事件规范化与消费者分发
 *
 * 职责：
 * 1. 监听 container 上的 pointer/wheel/keyboard/dblclick 事件
 * 2. 监听 window 上的全局 keyboard 事件
 * 3. 将 DOM 事件转换为规范化事件（NormalizedPointerEvent 等）
 * 4. 按 consumer.priority 降序分发，返回 true 则停止传播
 */

import * as THREE from 'three'
import type {
  InputConsumer,
  NormalizedKeyEvent,
  NormalizedPointerEvent,
  NormalizedWheelEvent,
  ViewportRect,
} from '@/shared/types/core'
import type {
  InputRouter as IInputRouter,
  RenderInvalidationService,
} from '@/shared/types/services'
import { screenToNDC as screenToNDCUtil } from '@/shared/utils/index'

type CompatibilityViewportProvider = (screen: THREE.Vector2) => ViewportRect | null | undefined

/** 从 container 元素获取视口矩形 */
function getViewportRect(container: HTMLElement): ViewportRect {
  const rect = container.getBoundingClientRect()
  return { x: rect.left, y: rect.top, width: rect.width, height: rect.height }
}

/** 输入路由器：规范化 DOM 事件并按优先级分发给消费者 */
export class InputRouter implements IInputRouter {
  /** 已注册的消费者列表（按 priority 降序排列） */
  private consumers: InputConsumer[] = []

  /** 当前指针是否按下 */
  private _isPointerDown = false

  /** 当前指针 NDC 坐标 */
  private _currentPointerNDC = new THREE.Vector2()

  /** 是否启用事件分发 */
  private _enabled = true

  /** 是否已释放 */
  private _disposed = false

  /** active-view / captured-view 兼容视口提供器 */
  private compatibilityViewportProvider: CompatibilityViewportProvider | null = null

  /** 绑定的事件处理器引用（用于移除监听） */
  private readonly boundHandlers: {
    pointerdown: (e: PointerEvent) => void
    pointermove: (e: PointerEvent) => void
    pointerup: (e: PointerEvent) => void
    pointercancel: (e: PointerEvent) => void
    wheelWake: (e: WheelEvent) => void
    wheel: (e: WheelEvent) => void
    keydown: (e: KeyboardEvent) => void
    keyup: (e: KeyboardEvent) => void
    winKeydown: (e: KeyboardEvent) => void
    winKeyup: (e: KeyboardEvent) => void
    dblclick: (e: PointerEvent) => void
  }

  constructor(
    private readonly container: HTMLElement,
    private readonly renderInvalidation?: RenderInvalidationService | null
  ) {
    // 确保 container 可接收键盘事件
    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '0')
    }

    // 绑定事件处理器
    this.boundHandlers = {
      pointerdown: this.handlePointerDown.bind(this),
      pointermove: this.handlePointerMove.bind(this),
      pointerup: this.handlePointerUp.bind(this),
      pointercancel: this.handlePointerCancel.bind(this),
      wheelWake: this.handleWheelWake.bind(this),
      wheel: this.handleWheel.bind(this),
      keydown: this.handleKeyDown.bind(this),
      keyup: this.handleKeyUp.bind(this),
      winKeydown: this.handleWinKeyDown.bind(this),
      winKeyup: this.handleWinKeyUp.bind(this),
      dblclick: this.handleDblClick.bind(this),
    }

    // 注册 DOM 事件监听
    container.addEventListener('pointerdown', this.boundHandlers.pointerdown, { passive: false })
    container.addEventListener('pointermove', this.boundHandlers.pointermove, { passive: false })
    container.addEventListener('pointerup', this.boundHandlers.pointerup, { passive: false })
    container.addEventListener('pointercancel', this.boundHandlers.pointercancel, {
      passive: false,
    })
    container.addEventListener('wheel', this.boundHandlers.wheelWake, {
      capture: true,
      passive: false,
    })
    container.addEventListener('wheel', this.boundHandlers.wheel, { passive: false })
    container.addEventListener('keydown', this.boundHandlers.keydown, { passive: false })
    container.addEventListener('keyup', this.boundHandlers.keyup, { passive: false })
    container.addEventListener('dblclick', this.boundHandlers.dblclick as EventListener, {
      passive: false,
    })
    window.addEventListener('keydown', this.boundHandlers.winKeydown, true)
    window.addEventListener('keyup', this.boundHandlers.winKeyup, true)
  }

  // ==================== 公共 API ====================

  /**
   * 注册输入消费者，按 priority 降序插入。
   *
   * @param consumer - 输入消费者
   */
  addConsumer(consumer: InputConsumer): void {
    this.consumers.push(consumer)
    this.sortConsumers()
  }

  /**
   * 移除输入消费者。
   *
   * @param consumer - 输入消费者；不存在时忽略
   */
  removeConsumer(consumer: InputConsumer): void {
    const idx = this.consumers.indexOf(consumer)
    if (idx !== -1) {
      this.consumers.splice(idx, 1)
    }
  }

  /**
   * 屏幕像素坐标转 NDC [-1, 1]。
   *
   * @param screenPos - 屏幕坐标，单位像素
   * @returns NDC 坐标
   */
  screenToNDC(screenPos: THREE.Vector2): THREE.Vector2 {
    const viewport = this.resolveCompatibilityViewport(screenPos)
    return screenToNDCUtil(screenPos, viewport)
  }

  /**
   * 设置 active-view / captured-view 兼容视口提供器。
   *
   * 新多视口逻辑必须改用 routed event，不能依赖这里的兼容视口。
   *
   * @param provider - 提供器；传 null 表示清除
   */
  setCompatibilityViewportProvider(provider: CompatibilityViewportProvider | null): void {
    this.compatibilityViewportProvider = provider
  }

  /** 当前指针是否按下 */
  get isPointerDown(): boolean {
    return this._isPointerDown
  }

  /** 当前指针 NDC 坐标 */
  get currentPointerNDC(): THREE.Vector2 {
    return this._currentPointerNDC.clone()
  }

  /** 设置是否启用事件分发，禁用时重置按压状态 */
  set enabled(value: boolean) {
    this._enabled = value
    if (!value) {
      this._isPointerDown = false
    }
  }

  /** 释放所有 DOM 监听和内部状态 */
  dispose(): void {
    if (this._disposed) return
    this._disposed = true

    this.container.removeEventListener('pointerdown', this.boundHandlers.pointerdown)
    this.container.removeEventListener('pointermove', this.boundHandlers.pointermove)
    this.container.removeEventListener('pointerup', this.boundHandlers.pointerup)
    this.container.removeEventListener('pointercancel', this.boundHandlers.pointercancel)
    this.container.removeEventListener('wheel', this.boundHandlers.wheelWake, true)
    this.container.removeEventListener('wheel', this.boundHandlers.wheel)
    this.container.removeEventListener('keydown', this.boundHandlers.keydown)
    this.container.removeEventListener('keyup', this.boundHandlers.keyup)
    this.container.removeEventListener('dblclick', this.boundHandlers.dblclick as EventListener)
    window.removeEventListener('keydown', this.boundHandlers.winKeydown, true)
    window.removeEventListener('keyup', this.boundHandlers.winKeyup, true)

    this.consumers.length = 0
  }

  // ==================== 内部方法 ====================

  /** 按 priority 降序排列消费者 */
  private sortConsumers(): void {
    this.consumers.sort((a, b) => b.priority - a.priority)
  }

  /**
   * 解析兼容层使用的视口。
   * 优先走 active-view / captured-view provider，缺失时回退到整个 container，
   * 以维持单视口场景与渐进迁移阶段的旧行为。
   */
  private resolveCompatibilityViewport(screen: THREE.Vector2): ViewportRect {
    const viewport = this.compatibilityViewportProvider?.(screen)
    if (viewport && viewport.width > 0 && viewport.height > 0) {
      return viewport
    }
    return getViewportRect(this.container)
  }

  /** 从 PointerEvent 构造规范化指针事件 */
  private normalizePointerEvent(e: PointerEvent): NormalizedPointerEvent {
    const screen = new THREE.Vector2(e.clientX, e.clientY)
    const viewport = this.resolveCompatibilityViewport(screen)
    const ndc = screenToNDCUtil(screen, viewport)
    return {
      screen,
      viewport,
      ndc,
      button: e.button,
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
      domEvent: e,
    }
  }

  /** 从 WheelEvent 构造规范化滚轮事件 */
  private normalizeWheelEvent(e: WheelEvent): NormalizedWheelEvent {
    const screen = new THREE.Vector2(e.clientX, e.clientY)
    const viewport = this.resolveCompatibilityViewport(screen)
    const ndc = screenToNDCUtil(screen, viewport)
    return {
      delta: e.deltaY,
      screen,
      viewport,
      ndc,
      domEvent: e,
    }
  }

  /** 从 KeyboardEvent 构造规范化键盘事件 */
  private normalizeKeyEvent(e: KeyboardEvent): NormalizedKeyEvent {
    return {
      key: e.key,
      code: e.code,
      shiftKey: e.shiftKey,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
      domEvent: e,
    }
  }

  /** 按优先级分发指针类事件 */
  private dispatchPointer(
    methodName: 'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onDoubleClick',
    event: NormalizedPointerEvent
  ): boolean {
    for (const consumer of this.consumers) {
      const handler = consumer[methodName]
      if (handler?.call(consumer, event)) {
        return true // 事件被消费，停止传播
      }
    }
    return false
  }

  /** 按优先级分发滚轮事件 */
  private dispatchWheel(event: NormalizedWheelEvent): boolean {
    for (const consumer of this.consumers) {
      if (consumer.onWheel?.(event)) {
        return true
      }
    }
    return false
  }

  /** 按优先级分发键盘事件 */
  private dispatchKey(methodName: 'onKeyDown' | 'onKeyUp', event: NormalizedKeyEvent): boolean {
    for (const consumer of this.consumers) {
      const handler = consumer[methodName]
      if (handler?.call(consumer, event)) {
        return true
      }
    }
    return false
  }

  /** 按优先级分发全局键盘事件 */
  private dispatchWinKey(
    methodName: 'onWinKeyDown' | 'onWinKeyUp',
    event: NormalizedKeyEvent
  ): boolean {
    for (const consumer of this.consumers) {
      const handler = consumer[methodName]
      if (handler?.call(consumer, event)) {
        return true
      }
    }
    return false
  }

  // ==================== DOM 事件处理 ====================

  private invalidateInput(reason: string): void {
    this.renderInvalidation?.invalidate(reason)
  }

  /** 处理指针按下 */
  private handlePointerDown(e: PointerEvent): void {
    if (!this._enabled) return
    this._isPointerDown = true
    const event = this.normalizePointerEvent(e)
    this._currentPointerNDC.copy(event.ndc)
    this.invalidateInput('input.pointerdown')
    this.dispatchPointer('onPointerDown', event)
  }

  /** 处理指针移动 */
  private handlePointerMove(e: PointerEvent): void {
    if (!this._enabled) return
    const event = this.normalizePointerEvent(e)
    this._currentPointerNDC.copy(event.ndc)
    if (this._isPointerDown) {
      this.invalidateInput('input.pointermove')
    }
    const consumed = this.dispatchPointer('onPointerMove', event)
    if (!this._isPointerDown && consumed) {
      this.invalidateInput('input.pointermove')
    }
  }

  /** 处理指针抬起 */
  private handlePointerUp(e: PointerEvent): void {
    if (!this._enabled) return
    this._isPointerDown = false
    const event = this.normalizePointerEvent(e)
    this._currentPointerNDC.copy(event.ndc)
    this.invalidateInput('input.pointerup')
    this.dispatchPointer('onPointerUp', event)
  }

  /** 在控制器截断滚轮事件前唤醒按需渲染。 */
  private handleWheelWake(_e: WheelEvent): void {
    if (!this._enabled) return
    this.invalidateInput('input.wheel')
  }

  /** 规范化并分发滚轮事件。 */
  private handleWheel(e: WheelEvent): void {
    if (!this._enabled) return
    const event = this.normalizeWheelEvent(e)
    this._currentPointerNDC.copy(event.ndc)
    this.dispatchWheel(event)
  }

  /** 处理键盘按下 */
  private handleKeyDown(e: KeyboardEvent): void {
    if (!this._enabled) return
    const event = this.normalizeKeyEvent(e)
    if (this.dispatchKey('onKeyDown', event)) {
      this.invalidateInput('input.keydown')
    }
  }

  /** 处理键盘抬起 */
  private handleKeyUp(e: KeyboardEvent): void {
    if (!this._enabled) return
    const event = this.normalizeKeyEvent(e)
    if (this.dispatchKey('onKeyUp', event)) {
      this.invalidateInput('input.keyup')
    }
  }

  /** 处理 window 键盘按下 */
  private handleWinKeyDown(e: KeyboardEvent): void {
    if (!this._enabled) return
    const event = this.normalizeKeyEvent(e)
    if (this.dispatchWinKey('onWinKeyDown', event)) {
      this.invalidateInput('input.windowKeydown')
    }
  }

  /** 处理 window 键盘抬起 */
  private handleWinKeyUp(e: KeyboardEvent): void {
    if (!this._enabled) return
    const event = this.normalizeKeyEvent(e)
    if (this.dispatchWinKey('onWinKeyUp', event)) {
      this.invalidateInput('input.windowKeyup')
    }
  }

  /** 处理指针取消（与 pointerup 行为一致，重置按压状态） */
  private handlePointerCancel(e: PointerEvent): void {
    if (!this._enabled) return
    this._isPointerDown = false
    const event = this.normalizePointerEvent(e)
    this._currentPointerNDC.copy(event.ndc)
    this.invalidateInput('input.pointercancel')
    this.dispatchPointer('onPointerUp', event)
  }

  /** 处理双击 */
  private handleDblClick(e: PointerEvent): void {
    if (!this._enabled) return
    const event = this.normalizePointerEvent(e)
    this._currentPointerNDC.copy(event.ndc)
    if (this.dispatchPointer('onDoubleClick', event)) {
      this.invalidateInput('input.dblclick')
      e.preventDefault()
    }
  }
}
